import React, { useState, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import AsyncSelect from "react-select/async";

export default function SearchBox({ modulesAccess }) {

  const location = useLocation();
  const history = useHistory();
  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState(null);

  const modules = useMemo(() => {
    const allModules = [];
    if (modulesAccess && Object.keys(modulesAccess).length) {
      for (const moduleKey in modulesAccess) {
        const module = modulesAccess[moduleKey];
        if (module.show_in_sidebar && module.permissions.length) {
          allModules.push({ label: module.module_name, value: module.slug, type: "Module" })
        }
      }
    }
    return allModules;
  }, [modulesAccess]);

  const hasCalendarAccess = useMemo(() => {
    if (modulesAccess && modulesAccess['calendar'] && modulesAccess['calendar'].permissions.length) {
      return true;
    }
    return false;
  }, [modulesAccess])

  const hasTasksAccess = useMemo(() => {
    if (modulesAccess && modulesAccess['tasks'] && modulesAccess['tasks'].permissions.length) {
      return true;
    }
    return false;
  }, [modulesAccess]);

  const hasQuotesAccess = useMemo(() => {
    if (modulesAccess && modulesAccess['quotes'] && modulesAccess['quotes'].permissions.length) {
      return true;
    }
    return false;
  }, [modulesAccess])

  const hasAccountsReceivablesAccess = useMemo(() => {
    if (modulesAccess && modulesAccess['accounts_receivable'] && modulesAccess['accounts_receivable'].permissions.length) {
      return true;
    }
  })

  const hasContractsAccess = useMemo(() => {
    if (modulesAccess && modulesAccess['contracts'] && modulesAccess['contracts'].permissions.length) {
      return true;
    }
    return false;
  }, [modulesAccess])

  const hasTimesheetsAccess = useMemo(() => {
    if (modulesAccess && modulesAccess['timesheet'] && modulesAccess['timesheet'].permissions.length) {
      return true;
    }
    return false;
  }, [modulesAccess])

  const hasRFQAccess = useMemo(() => {
    if (modulesAccess && modulesAccess['request_for_quotes'] && modulesAccess['request_for_quotes'].permissions.length) {
      return true;
    }
    return false;
  }, [modulesAccess])


  const loadOptions = (inputValue, callback) => {
    const allModules = [...modules];
    if (hasCalendarAccess) {
      allModules.push({ label: "Job Listing", value: "calendar", type: "Module", extra: { goToTab: "Job Listing" } })
    }
    const searchModules = allModules.filter(v => v.label.toLowerCase().includes(inputValue.toLowerCase()));
    if (hasCalendarAccess) {
      if (inputValue && /^J[0-9]{1,}/gmi.test(inputValue)) {
        searchModules.push({ label: `${inputValue.toUpperCase()} - Search ${inputValue.toUpperCase()} in Jobs`, value: "calendar", type: "Search", extra: { goToTab: "Job Listing", searchParam: inputValue.toUpperCase() } })
      }
      if (hasTimesheetsAccess) {
        if (inputValue && /^J[0-9]{1,}/gmi.test(inputValue)) {
          searchModules.push({ label: `${inputValue.toUpperCase()} - Search ${inputValue} in Timesheets`, value: "timesheet", type: "Search", extra: { searchParam: inputValue } })
        }
      }
      if (hasAccountsReceivablesAccess) {
        if (inputValue && /^J[0-9]{1,}/gmi.test(inputValue)) {
          searchModules.push({ label: `${inputValue.toUpperCase()} - Search ${inputValue.toUpperCase()} in Accounts Receivables`, value: "accounts_receivable", type: "Search", extra: { searchParam: inputValue.toUpperCase() } })
        }
      }
    }

    if (hasContractsAccess) {
      if (inputValue && /^C[0-9]{1,}/gmi.test(inputValue)) {
        searchModules.push({ label: `${inputValue.toUpperCase()} - Search ${inputValue.toUpperCase()} in Contracts`, value: "contracts", type: "Search", extra: { searchParam: inputValue.toUpperCase() } })
      }
    }

    if (hasRFQAccess) {
      if (inputValue && /^RFQ[0-9]{1,}/gmi.test(inputValue)) {
        searchModules.push({ label: `${inputValue.toUpperCase()} - Search ${inputValue.toUpperCase()} in RFQs`, value: "request_for_quotes", type: "Search", extra: { searchParam: inputValue.toUpperCase() } })
      }
    }

    if (hasTasksAccess) {
      if (inputValue && /^T[0-9]{1,}/gmi.test(inputValue)) {
        searchModules.push({ label: `${inputValue.toUpperCase()} - Search ${inputValue.toUpperCase()} in Tasks`, value: "tasks", type: "Search", extra: { searchParam: inputValue.toUpperCase() } })
      }
    }

    if (hasQuotesAccess) {
      if (inputValue && /^Q[0-9]{1,}/gmi.test(inputValue)) {
        searchModules.push({ label: `${inputValue.toUpperCase()} - Search ${inputValue.toUpperCase()} in Quotes`, value: "quotes", type: "Search", searchParam: inputValue.toUpperCase() })
      }
    }

    callback(searchModules)
  }

  const DownChevron = ({ }) => (
    <div className="d-flex align-items-center justify-content-center" style={{ padding: "0px 8px" }}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height={16} width={16} style={{ marginTop: "-2px" }}>
        <path fill="#362148bd" d="M480 272C480 317.9 465.1 360.3 440 394.7L566.6 521.4C579.1 533.9 579.1 554.2 566.6 566.7C554.1 579.2 533.8 579.2 521.3 566.7L394.7 440C360.3 465.1 317.9 480 272 480C157.1 480 64 386.9 64 272C64 157.1 157.1 64 272 64C386.9 64 480 157.1 480 272zM272 416C351.5 416 416 351.5 416 272C416 192.5 351.5 128 272 128C192.5 128 128 192.5 128 272C128 351.5 192.5 416 272 416z" />
      </svg>
    </div>
  )

  const onChange = (v) => {
    setValue(null);
    setInputValue("");
    if (v && v.type && v.type === "Module") {
      history.push({ pathname: `/dashboard/${v.value}`, ...(v.extra ? v.extra : {}) });
    } else {
      if (v && v.type && v.type === "Search") {
        const pathName = `/dashboard/${v.value}`;
        if (location.pathname === pathName) {
          history.replace({ pathname: pathName, searchParam: v.searchParam, ...(v.extra ? v.extra : {}) });
        } else {
          history.push({ pathname: `/dashboard/${v.value}`, searchParam: v.searchParam, ...(v.extra ? v.extra : {}) })
        }
      }
    }
  }

  return (
    <div style={{ minWidth: "400px" }}>
      <AsyncSelect
        onChange={onChange}
        isSearchable
        value={value}
        inputValue={inputValue}
        onInputChange={(val) => setInputValue(val)}
        isClearable
        noOptionsMessage={({ inputValue }) => inputValue.length ? "Not Found" : "Type to Search for Modules, Jobs, Sites, etc."}
        autoFocus
        cacheOptions
        loadOptions={loadOptions}
        placeholder="Search Modules, Jobs, Quotes, RFQs, Tasks, Contracts..."
        styles={searchStyles}
        components={{ DropdownIndicator: DownChevron }}
      />
    </div>
  )
}

const searchStyles = {
  control: (styles, { isFocused, menuIsOpen, isDisabled }) => {
    return {
      ...styles,
      backgroundColor: isDisabled ? '#e7e7e7 !important' : '#fff !important',
      boxShadow: isFocused || menuIsOpen ? '0 0 0 0.05rem #c0b4ff' : 'none',
      border: '1px solid #ddd',
      fontSize: '14px',
      fontWeight: "500",
      borderRadius: '10px',
      minHeight: 'auto',
      fontFamily: "'Poppins', sans-serif",
      height: '32px',
      "&:hover": {
        border: '1px solid #dddddd',
      },
      fontSize: "13px !important"
    }
  },
  indicatorsContainer: (styles, state) => ({
    ...styles,
    height: 'inherit',
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0px 5px"
  }),
  indicatorSeparator: (styles, state) => ({
    ...styles,
    backgroundColor: '#36214880'
  }),
  dropdownIndicator: (styles, state) => ({
    ...styles,
    color: '#36214880'
  }),
  valueContainer: (styles, state) => ({
    ...styles,
    display: "flex",
    flexDirection: "row",
    overflowX: "hidden",
    paddingTop: '0px',
    paddingBottom: '0px',
    flexWrap: "nowrap",
    fontSize: "13px",
    height: "inherit",
    fontFamily: "'Poppins', sans-serif",
  }),
  singleValue: (styles) => ({
    ...styles,
    fontSize: "13px !important"
  }),
  placeholder: (styles, state) => ({
    ...styles,
    color: '#362148 !important',
    fontWeight: "500",
    fontFamily: '"Poppins", sans-serif',
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    wordBreak: "break-all",
    overflow: "hidden",
    width: '-webkit-fill-available'
  }),
  multiValue: (styles, state) => ({
    ...styles,
    minWidth: "auto"
  }),
  loadingIndicator: (styles, state) => ({
    ...styles,
    padding: "0px",
  }),
  loadingDot: (styles, state) => ({
    width: "8px",
    height: "8px"
  }),
  menu: (styles, state) => ({
    ...styles,
    zIndex: 7,
    width: "600px",
    left: "50%",
    transform: "translateX(-50%)"
  }),
  noOptionsMessage: (styles, state) => ({
    ...styles,
    fontFamily: "'Poppins', sans-serif",
    fontSize: "14px",
    color: "#999",
    fontWeight: 500
  }),
  option: (styles, { isFocused, isSelected }) => {
    return {
      ...styles,
      color: isSelected ? '#fff !important' : '#362148 !important',
      padding: '6px 5px',
      alignItems: 'center',
      width: 'auto',
      fontFamily: '"Poppins", sans-serif',
      marginBottom: '0px',
      fontSize: "14px",
      display: 'flex',
      fontWeight: 500,
      lineHeight: '22px',
      backgroundColor: isSelected ? '#c0b4ff !important' : isFocused ? '#c0b4ff80 !important' : '#fff !important',
      "&:hover": {
        backgroundColor: (isSelected) ? '#c0b4ff !important' : '#c0b4ff80 !important',
      },
    };
  },
};
